import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import PermissionsContainer from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import safetySystemService from '../../services/safetySystem.service';
import type { SafetyTableOptions, SafetyTableRow, SafetyTableSpec } from '../../types/safetySystem';
import SafetyTableDrawer from './components/SafetyTableDrawer';
import { gridColumns, norm, tableComponentId } from './components/tableForm';

/**
 * Safety Tables — port of the V1 Safety "Table Maintenance" menu (Phase 5). One page, a
 * table picker (the catalog the server says the caller may see), the table's grid built
 * from its spec, and the spec-driven drawer. URL state: `?table=<key>&deleted=1&open=<recno|new>`.
 * Entries maintained elsewhere in 360 (`external_route`) show a link card instead of a grid.
 */

const ACCESS_DENIED = 'You do not have access to this table.';
const byTitle = (a: SafetyTableSpec, b: SafetyTableSpec) => a.title.localeCompare(b.title);

function tablesErrorMessage(error: unknown, fallback: string): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) return ACCESS_DENIED;
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

export default function SafetyTables() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageComponents } = usePermissionsMode();

  // ---- Catalog -------------------------------------------------------------
  const [catalog, setCatalog] = useState<SafetyTableSpec[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [catalogTick, setCatalogTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setCatalogLoading(true);
    setCatalogError('');
    safetySystemService.tablesCatalog(controller.signal)
      .then((res) => { if (!controller.signal.aborted) setCatalog([...(res.data ?? [])].sort(byTitle)); })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setCatalog([]);
        setCatalogError(tablesErrorMessage(e, 'Unable to load the table list. Retry or check your access with an administrator.'));
      })
      .finally(() => { if (!controller.signal.aborted) setCatalogLoading(false); });
    return () => controller.abort();
  }, [catalogTick]);

  // One permission component per maintained table (link-outs carry none).
  const pageComponentsList = useMemo(
    () => catalog.filter((t) => !t.external_route).map((t) => ({ id: tableComponentId(t.key), name: t.title })),
    [catalog],
  );
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // ---- URL state -----------------------------------------------------------
  const tableKey = searchParams.get('table') || '';
  const includeDeleted = searchParams.get('deleted') === '1';
  const openParam = searchParams.get('open') || '';
  const setParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const spec = useMemo(() => catalog.find((t) => t.key === tableKey) ?? null, [catalog, tableKey]);
  const pickerOptions = useMemo(() => catalog.map((t) => ({ value: t.key, label: t.title })), [catalog]);

  // ---- Rows ----------------------------------------------------------------
  const [rows, setRows] = useState<SafetyTableRow[]>([]);
  const [options, setOptions] = useState<SafetyTableOptions>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const gridKey = spec && !spec.external_route ? spec.key : '';
  useEffect(() => {
    if (!gridKey) { setRows([]); setOptions({}); setError(''); setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    safetySystemService.tableRows(gridKey, { includeDeleted }, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setRows(res.rows ?? []);
        setOptions(res.options ?? {});
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(tablesErrorMessage(e, 'Unable to load this table. Retry or check your access with an administrator.'));
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [gridKey, includeDeleted, reloadTick]);

  const columns = useMemo(() => (spec ? gridColumns<SafetyTableRow>(spec) : []), [spec]);
  const soft = spec?.soft_delete ?? null;
  const rowClass = useCallback((r: SafetyTableRow) => (soft && norm(r[soft]).toUpperCase() === 'Y' ? 'text-muted' : ''), [soft]);

  // ---- Drawer --------------------------------------------------------------
  const canAdd = !!spec && !spec.external_route && spec.level >= 2 && !spec.readonly && !spec.no_create;
  const canOpen = !!spec && !spec.external_route && !spec.readonly;
  const openRecno: number | 'new' | null = !canOpen || !openParam ? null
    : openParam === 'new' ? (canAdd ? 'new' : null)
      : /^\d+$/.test(openParam) ? Number(openParam) : null;
  const closeDrawer = useCallback(() => setParams({ open: null }), [setParams]);
  const onSaved = useCallback(() => setReloadTick((t) => t + 1), []);

  return (
    <main className="flex h-[calc(100vh-7rem)] min-h-[450px] flex-col gap-2 overflow-hidden p-2 sm:p-4 text-gray-900 dark:text-gray-100">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Safety Tables</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Pick a lookup table to view or maintain its rows. Click a row to open it.</p>
        </div>
        <button
          type="button"
          onClick={() => { setCatalogTick((t) => t + 1); setReloadTick((t) => t + 1); }}
          disabled={catalogLoading || loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
        >
          <ArrowPathIcon width={16} aria-hidden="true" />
          {catalogLoading || loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>
      {catalogError && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{catalogError}</div>}

      <FilterBar wrap label="" className="shrink-0">
        <FilterBar.Group label="Table">
          <SingleSelect ariaLabel="Table" value={tableKey} onChange={(v) => setParams({ table: v, open: null })}
            options={pickerOptions} size="sm" className="w-72" usePortal enableSearch placeholder={catalogLoading ? 'Loading…' : 'Select a table…'} />
        </FilterBar.Group>
        {spec && !spec.external_route && spec.soft_delete && (
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setParams({ deleted: e.target.checked ? '1' : null })}
              className="h-4 w-4 rounded border-rule-strong" />
            Include deleted
          </label>
        )}
      </FilterBar>

      {!spec && (
        <p className="rounded-md border border-rule bg-panel p-4 text-sm text-gray-500 dark:text-gray-400">
          {catalogLoading ? 'Loading tables…' : catalog.length === 0 && !catalogError ? 'No tables are available to you.' : 'Select a table from the list.'}
        </p>
      )}

      {spec?.external_route && (
        <section aria-label={spec.title} className="rounded-md border border-rule bg-panel p-4 text-sm">
          <h2 className="text-base font-semibold">{spec.title}</h2>
          <p className="mt-1 text-gray-500 dark:text-gray-400">Maintained in Table Maintenance.</p>
          <Link to={spec.external_route} className="mt-2 inline-flex items-center gap-1 font-medium text-accent-text hover:underline">
            Open {spec.title}<ArrowTopRightOnSquareIcon width={14} aria-hidden="true" />
          </Link>
        </section>
      )}

      {spec && !spec.external_route && (
        <>
          {error && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}
          <PermissionsContainer componentId={tableComponentId(spec.key)} componentName={spec.title} defaultHidden className="min-h-0 flex-1">
            <TanTable<SafetyTableRow>
              data={rows}
              columns={columns}
              loading={loading}
              fillContainer
              cardTitle={spec.title}
              settingsKey={`safety-table-${spec.key}`}
              fieldName={`safety-table-${spec.key}`}
              searchPlaceholder="Filter rows…"
              enableColumnFilters
              enableExcelExport
              enableShowHideCols
              enableRowsPerPage
              enableZebraStriping
              defaultPageSize={100}
              getRowId={(r) => String(r.recno)}
              getRowClassName={rowClass}
              onRowClick={canOpen ? (r) => setParams({ open: String(r.recno) }) : undefined}
              onEditFilters={canAdd ? () => setParams({ open: 'new' }) : undefined}
              editFiltersButtonIcon="plus"
            />
          </PermissionsContainer>
        </>
      )}

      {spec && openRecno !== null && (
        <SafetyTableDrawer
          key={`${spec.key}:${openRecno}`}
          tableKey={spec.key}
          recno={openRecno}
          spec={spec}
          options={options}
          includeDeleted={includeDeleted}
          onClose={closeDrawer}
          onSaved={onSaved}
        />
      )}
    </main>
  );
}
